import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Stepper, Step, StepLabel } from "@mui/material";
import { useFlags } from "launchdarkly-react-client-sdk";

import useView from "components/Hooks/useViewHook";
import PageLoader from "components/common/PageLoader";
import GetHelpSection from "components/Screens/CustomerOnboarding/GetHelpSection";

import { ONBOARDING_STEP_DISPLAY_INFO } from "components/Screens/CustomerOnboarding/constants";
import { getUserAdditionalInfo } from "selectors/user";

import "./CustomerOnboardingSidebar.scss";

export function getStrokeColor(isPaymentSuccess, isCreateMeetingStarted, isMeetingCreated) {
  if (isPaymentSuccess) {
    if (isMeetingCreated) return "var(--color-border-success)";
    if (isCreateMeetingStarted) return "var(--color-surface-secondary-inverse)";
    return "var(--color-border-success)";
  }

  return "var(--color-surface-secondary-inverse)";
}

function DesktopStepper({ visibleSteps, isPaymentSuccess, isCreateMeetingStarted, isMeetingCreated }) {
  const getStepClass = (isStepCompleted, isStepActive) => {
    if (isStepCompleted) return "completed";
    if (isStepActive) return "active";
    return "inactive";
  };

  return (
    <div className="customer-onboarding-desktop-view-main-div">
      <div className="desktop-stepper">
        <p className="pb-15 pt-2 color-text-heading-primary heading-h4-semibold">
          Complete these steps to set up your account
        </p>
        <Stepper activeStep={1} orientation="vertical">
          {visibleSteps.map(({ label, description, slug }, index) => {
            let isStepCompleted = false;
            if (isPaymentSuccess && slug === "add-payment") {
              isStepCompleted = true;
            }
            if (isMeetingCreated && slug === "calendly-meeting") {
              isStepCompleted = true;
            }

            const isStepActive =
              (!isStepCompleted && index === 0) ||
              (!isStepCompleted && visibleSteps[index - 1] && isPaymentSuccess && slug === "calendly-meeting") ||
              ((isMeetingCreated || isCreateMeetingStarted) && slug === "calendly-meeting");

            return (
              <Step key={slug} completed={isStepCompleted}>
                <StepLabel className={`custom-step-label ${getStepClass(isStepCompleted, isStepActive)}`}>
                  <div
                    className={`stepper-content ${isStepActive ? "active" : "inactive"}`}
                    style={{
                      borderColor: getStrokeColor(isPaymentSuccess, isCreateMeetingStarted, isMeetingCreated),
                    }}
                  >
                    <p className="para-body-m-semibold color-text-heading-primary">{label}</p>
                    <p className="para-body-s-regular color-text-body-secondary">{description}</p>
                  </div>
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </div>

      <GetHelpSection />
    </div>
  );
}

function MobileStepperIndicator({
  visibleSteps,
  isPaymentSuccess,
  isCreateMeetingStarted,
  isMeetingCreated,
  currentStep,
  totalSteps,
  nextStep,
}) {
  const stepDetails = visibleSteps[currentStep - 1];

  if (!stepDetails) return null;

  return (
    <div className="onboarding-mobile-view-main-div">
      <p className="color-text-heading-primary heading-h4-semibold">Complete these steps to set up your account</p>
      <div className="mobile-step-indicator">
        <div className="step-progress">
          <div className="progress-ring">
            <svg width="48" height="48">
              <circle className="progress-ring-circle-bg" strokeWidth="4" fill="transparent" r="20" cx="24" cy="24" />
              <circle
                className="progress-ring-circle"
                strokeWidth="4"
                fill="transparent"
                r="20"
                cx="24"
                cy="24"
                stroke={getStrokeColor(isPaymentSuccess, isCreateMeetingStarted, isMeetingCreated)}
                strokeDasharray={`${(currentStep / totalSteps) * 125.6} 125.6`}
              />
            </svg>
            <div className="step-count">
              {currentStep} of {totalSteps}
            </div>
          </div>
        </div>
        <div>
          <h2 className="heading-h5-semibold color-text-heading-primary p-0 m-0">{stepDetails.label}</h2>
          <p className="para-body-s-regular color-text-heading-primary">{stepDetails.description}</p>
          {nextStep && (
            <div className="next-step">
              <span className="para-body-s-regular color-text-body-primary">Next: </span>
              <span className="para-body-s-regular color-text-body-primary">{nextStep}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CustomerOnboardingSidebar({ isPaymentSuccess, isCreateMeetingStarted, isMeetingCreated }) {
  const { isMobile, isDesktop } = useView();
  const { skipKickoffCall: skipKickoffCallFlag } = useFlags();
  const loadingAddBankAccountInfo = useSelector((state) => state.AddBankAccountInfo.loadingAddBankAccountInfo);
  const userAdditionalInfo = useSelector(getUserAdditionalInfo);

  const visibleSteps = useMemo(() => {
    const backendSteps = userAdditionalInfo?.onboardingProgress?.steps || [];
    const activeSteps = backendSteps.filter((step) => !(skipKickoffCallFlag && step.skipped));

    return activeSteps.map((step) => ({
      slug: step.slug,
      ...(ONBOARDING_STEP_DISPLAY_INFO[step.slug] || { label: step.name, description: step.description }),
    }));
  }, [userAdditionalInfo?.onboardingProgress?.steps, skipKickoffCallFlag]);

  const totalSteps = visibleSteps.length;
  const currentStep = isPaymentSuccess && visibleSteps.some((s) => s.slug === "calendly-meeting") ? 2 : 1;
  const nextStepLabel = !isPaymentSuccess && visibleSteps.length > 1 ? visibleSteps[1]?.label : "";

  return (
    <>
      {loadingAddBankAccountInfo && <PageLoader />}

      {isDesktop && (
        <DesktopStepper
          visibleSteps={visibleSteps}
          isPaymentSuccess={isPaymentSuccess}
          isCreateMeetingStarted={isCreateMeetingStarted}
          isMeetingCreated={isMeetingCreated}
          helpSection={<GetHelpSection />}
        />
      )}
      {isMobile && (
        <MobileStepperIndicator
          visibleSteps={visibleSteps}
          isPaymentSuccess={isPaymentSuccess}
          isCreateMeetingStarted={isCreateMeetingStarted}
          isMeetingCreated={isMeetingCreated}
          currentStep={currentStep}
          totalSteps={totalSteps}
          nextStep={nextStepLabel}
        />
      )}
    </>
  );
}

export default CustomerOnboardingSidebar;
